import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { GlossyCross } from "./GlossyCross";
import { TID } from "../lib/testIds";

export const Header = () => {
  const { t, lang, setLang, langs } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-xs tracking-widest2 uppercase transition-colors duration-300 ${
      isActive ? "text-brand-platinum" : "text-brand-silver hover:text-brand-platinum"
    }`;

  return (
    <header
      className={`transition-all duration-500 ${
        scrolled ? "bg-brand-bg/85 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-24">
        <Link to="/" data-testid={TID.nav.logo} className="flex items-center gap-3 group">
          <img
            src="/media/logo.png"
            alt="Swiss AlpenLine"
            className="h-16 md:h-20 lg:h-24 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          <NavLink to="/" end className={linkClass} data-testid={TID.nav.home}>{t.nav.home}</NavLink>
          <NavLink to="/servicos" className={linkClass} data-testid={TID.nav.services}>{t.nav.services}</NavLink>
          <NavLink to="/reservar" className={linkClass} data-testid={TID.nav.reserve}>{t.nav.reserve}</NavLink>
          <NavLink to="/contacto" className={linkClass} data-testid={TID.nav.contact}>{t.nav.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              data-testid={TID.nav.langSelector}
              className="flex items-center gap-1.5 text-xs tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum transition-colors"
            >
              {lang.toUpperCase()}
              <ChevronDown size={12} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-3 min-w-[120px] bg-brand-surface border border-brand-border py-2">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    data-testid={`lang-option-${l.code}`}
                    className={`block w-full text-left px-4 py-2 text-xs tracking-widest uppercase transition-colors ${
                      lang === l.code ? "text-brand-platinum" : "text-brand-silver hover:text-brand-platinum"
                    }`}
                  >
                    {l.full}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => navigate("/reservar")}
            data-testid={TID.nav.reserve}
            className="hidden md:inline-flex btn-pill btn-pill--solid"
          >
            {t.nav.reserve}
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={TID.nav.menuMobile}
            className="lg:hidden text-brand-platinum"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-brand-surface border-t border-brand-border">
          <div className="flex flex-col px-6 py-6 gap-5">
            {[
              ["/", t.nav.home],
              ["/servicos", t.nav.services],
              ["/reservar", t.nav.reserve],
              ["/contacto", t.nav.contact],
            ].map(([to, label]) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className="text-sm tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum transition-colors"
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
